import React, { useMemo, useState } from 'react';
import { Star, BadgeCheck, Sparkles, AlertTriangle, ShieldAlert } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { VEHICLE_LABELS } from '@/lib/format';

// Interactive or read-only 1..5 star rating.
export const StarRating = ({ value = 0, onChange, size = 26, readOnly = false, testId = 'star-rating' }) => {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="inline-flex items-center gap-1" role={readOnly ? 'img' : 'radiogroup'} aria-label={`${value} of 5 stars`} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= active;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange && onChange(n)}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            data-testid={`${testId}-${n}`}
            className="transition-transform"
            style={{
              lineHeight: 0,
              cursor: readOnly ? 'default' : 'pointer',
              transform: !readOnly && n <= hover ? 'scale(1.12)' : 'scale(1)',
              background: 'transparent',
              border: 'none',
              padding: readOnly ? 0 : 2,
            }}
          >
            <Star
              size={size}
              style={{
                color: filled ? 'var(--warning, #F5A524)' : 'var(--border)',
                fill: filled ? 'var(--warning, #F5A524)' : 'transparent',
              }}
            />
          </button>
        );
      })}
    </div>
  );
};

// Compact trust badge summarizing a vehicle's community signal.
export const VehicleTrustBadge = ({ trust, className = '' }) => {
  const t = useT();
  const vehicleTypes = useMemo(
    () => (trust && Array.isArray(trust.vehicle_types) ? trust.vehicle_types.filter(Boolean) : []),
    [trust]
  );
  if (!trust) return null;
  const seen = trust.times_seen || 0;
  const isNew = seen <= 1;
  const avg = trust.rating_avg;
  const ratingCount = trust.rating_count || 0;
  const warningLevel = trust.warning_level || 'none';
  const dangerCount = trust.danger_count || 0;
  const topDanger = Array.isArray(trust.top_danger_tags) ? trust.top_danger_tags : [];
  const topPositive = Array.isArray(trust.top_positive_tags) ? trust.top_positive_tags : [];

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid="vehicle-trust-badge">
      {warningLevel !== 'none' && (
        <div
          className="rounded-xl px-3 py-2.5 flex items-start gap-2"
          style={{
            background: warningLevel === 'danger'
              ? 'color-mix(in srgb, var(--danger) 14%, transparent)'
              : 'color-mix(in srgb, var(--warning) 16%, transparent)',
            border: `1px solid ${warningLevel === 'danger' ? 'var(--danger)' : 'var(--warning)'}`,
          }}
          data-testid="vehicle-danger-warning"
        >
          {warningLevel === 'danger' ? <ShieldAlert size={18} style={{ color: 'var(--danger)', flexShrink: 0 }} /> : <AlertTriangle size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />}
          <div>
            <p className="text-xs font-bold" style={{ color: warningLevel === 'danger' ? 'var(--danger)' : 'var(--warning)' }}>
              {warningLevel === 'danger' ? t('vt_warn_danger') : t('vt_warn_caution')}
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {t('vt_warn_reports').replace('{n}', dangerCount)}
            </p>
            {topDanger.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {topDanger.map((tg) => (
                  <span key={tg} className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: 'var(--surface)', color: 'var(--danger)', border: '1px solid var(--danger)' }}>
                    {t(`tag_${tg}`)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className="inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-xs font-bold"
        style={{
          background: isNew ? 'var(--surface-alt)' : 'color-mix(in srgb, var(--success) 14%, transparent)',
          color: isNew ? 'var(--text-muted)' : 'var(--success)',
        }}
        data-testid="vehicle-trust-info"
      >
        {isNew ? <Sparkles size={14} /> : <BadgeCheck size={15} />}
        {seen > 1 ? t('vp_seen_times').replace('{n}', seen) : t('vp_seen_first')}
      </div>

      {avg != null && ratingCount > 0 && (
        <div className="flex items-center gap-2" data-testid="vehicle-trust-rating">
          <StarRating value={Math.round(avg)} readOnly size={16} testId="vehicle-trust-stars" />
          <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{avg.toFixed(1)}</span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {t('vt_rating_count').replace('{n}', ratingCount)}
          </span>
        </div>
      )}

      {topPositive.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="vehicle-positive-tags">
          {topPositive.map((tg) => (
            <span key={tg} className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: 'color-mix(in srgb, var(--success) 12%, transparent)', color: 'var(--success)' }}>
              {t(`tag_${tg}`)}
            </span>
          ))}
        </div>
      )}

      {vehicleTypes.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="vehicle-trust-types">
          {vehicleTypes.map((vt) => (
            <span
              key={vt}
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--surface)' }}
            >
              {VEHICLE_LABELS[vt] || vt}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
